// @ts-check
import { gql, useQuery } from '@apollo/client';
import { Trans } from '@lingui/macro';
import Moment from 'moment';
import React from 'react';
import { useParams } from 'react-router-dom';
import {
  Box,
  Button,
  DeleteIcon,
  DownloadIcon,
  Flex,
  Menu,
  MenuButton,
  MenuItem,
  MenuLink,
  MenuList,
  SkeletonBox,
  TableCell,
  TableRow,
  Text,
} from '~/design-system';
import { useCommonLinks } from '../../../pages/imports/_config';
import ImportTypeLabel from '../ImportTypeLabel';

/**
 * Used to render import in progress
 * @param {Object} props
 * @param {Object} props.oneImport - the import data
 * @param {function} props.onDelete - when delete button is clicked
 */
export default function ImportInProgressRow({ oneImport, onDelete }) {
  const { workspaceId } = useParams();
  const { importId, type: importType } = oneImport;
  const link = useCommonLinks();
  const { data, startPolling, stopPolling } = useQuery(query, {
    variables: {
      workspaceId,
      importId,
    },
  });
  const state = data?.workspace?.dataImport?.state?.state;
  React.useEffect(() => {
    if (state === 'JOB_IN_PROGRESS') {
      startPolling(1000);
    }
    return () => {
      stopPolling();
    };
  }, [data, state, startPolling, stopPolling]);

  if (state && state !== 'JOB_IN_PROGRESS') {
    stopPolling();
  }

  return (
    <TableRow>
      <TableCell
        sx={{
          height: 65,
          width: 260,
        }}
      >
        <Text>{oneImport.name}</Text>
        <ImportTypeLabel type={oneImport.type} />
      </TableCell>
      <TableCell sx={{ height: 65, width: 240 }}>
        <Text variant="small">{oneImport.creator?.fullName}</Text>
        <Text variant="smallest">{oneImport.creator?.email}</Text>
      </TableCell>
      <TableCell sx={{ height: 65, width: 180 }}>
        <Text>{Moment(oneImport.createdAt).format('LL')}</Text>
        <Text variant="smallest">
          {Moment(oneImport.createdAt).format('LT')}
        </Text>
      </TableCell>
      <TableCell sx={{ height: 65 }}>
        {!state && (
          <Flex sx={{ alignItems: 'center' }}>
            <SkeletonBox
              height={22}
              width={48}
              sx={{ marginRight: 3, marginLeft: 1 }}
            />
            <SkeletonBox height={22} width={30} />
          </Flex>
        )}

        {state && (
          <Flex sx={{ alignItems: 'center' }}>
            <Button
              sx={{ marginLeft: 2, paddingY: 0, paddingX: 1, fontSize: 12 }}
              variant="secondary"
              to={link.import({ importId, importType })}
            >
              {state === 'JOB_IN_PROGRESS' ? (
                <Trans>
                  {data?.workspace?.dataImport?.state?.progress}% progress{' '}
                </Trans>
              ) : (
                <Trans>Continue</Trans>
              )}
            </Button>

            <Menu>
              <MenuButton variant="icon">
                <Box
                  sx={{
                    border: '1px solid #ccc',
                    borderRadius: 3,
                    paddingY: 0,
                    paddingX: 2,
                  }}
                >
                  ···
                </Box>
              </MenuButton>
              <MenuList
                sx={{
                  zIndex: 2,
                  position: 'relative',
                }}
              >
                <MenuLink href={oneImport.source.url}>
                  <DownloadIcon sx={{ marginRight: 2, marginTop: -1 }} />
                  <Trans>Download source file</Trans>
                </MenuLink>
                {state !== 'JOB_IN_PROGRESS' && (
                  <MenuItem
                    onSelect={() => onDelete(oneImport)}
                    sx={{ color: 'brand.warningRed' }}
                  >
                    <DeleteIcon
                      sx={{ marginRight: 2, marginLeft: 1, marginTop: -1 }}
                    />
                    <Trans>Delete</Trans>
                  </MenuItem>
                )}
              </MenuList>
            </Menu>
          </Flex>
        )}
      </TableCell>
    </TableRow>
  );
}

const query = gql`
  query ImportInProgressRow($workspaceId: ID!, $importId: ID!) {
    workspace(workspaceId: $workspaceId) {
      workspaceId
      dataImport(importId: $importId) {
        importId
        state {
          state
          progress
        }
      }
    }
  }
`;
